import { Box, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import './Calendar.css'
import dashboard_ from '../../sass/dashboard.module.sass'
import { t } from 'i18next'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material'
import { UsageStatsTable } from './UsageStatsTable'
import { useDispatch } from 'react-redux'
//import { mainCalendarRangeSearch } from '../../store/user/actionCreator'
import { api } from '../../function/axios'
import UsageStatsAll from './UsageStatsAll'


type BadgeMap = Record<string, number>

const daysInMonth = (year: number, month: number): number =>
    new Date(year, month + 1, 0).getDate()

const getToday = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const formatYMD = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

export function Calendar() {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogContent, setDialogContent] = useState<any[]>([])
    const [currentDate, setCurrentDate] = useState(new Date())
    const [dateFrom, setDateFrom] = useState<string | null>(null)
    const [dateTo, setDateTo] = useState<string | null>(null)

    const [calendar, setCalendar] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const days = daysInMonth(year, month)
    const startDay = (new Date(year, month, 1).getDay() + 6) % 7

    const dispatch = useDispatch()
    const monthRange = useMemo(() => {
        const start = new Date(year, month, 1)
        const end = new Date(year, month + 1, 0)
        return { start: formatYMD(start), end: formatYMD(end) }
    }, [year, month])

    useEffect(() => {
        let cancelled = false
        const controller = new AbortController()

        async function loadCalendar() {
            try {
                setLoading(true)
                setError(null)

                const res = await api({
                    method: 'GET',
                    url: `/admin-dashboard/common-calendar`,
                    params: { start: monthRange.start, end: monthRange.end },
                })
                const data = res?.data
                if (!cancelled) {
                    setCalendar(Array.isArray(data) ? data : data?.calendar ?? [])
                }
            } catch (e: any) {
                if (!cancelled && e.name !== 'CanceledError') {
                    setError(e?.message || 'Failed to load')
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        loadCalendar()
        return () => {
            cancelled = true
            controller.abort()
        }
    }, [monthRange.start, monthRange.end])


    const handleDialogClose = () => {
        setDialogOpen(false)
    }

    const badges: BadgeMap = Object.fromEntries(
        calendar?.map((item: any) => [item?.date, Number(item?.count)]),
    )

    const handleDateClick = (dateStr: string) => {
        if (!dateFrom || (dateFrom && dateTo)) {
            setDateFrom(dateStr)
            setDateTo(null)
        } else {
            if (new Date(dateStr) < new Date(dateFrom)) {
                setDateTo(dateFrom)
                setDateFrom(dateStr)
            } else {
                setDateTo(dateStr)
            }
        }
    }

    const isInRange = (dateStr: string): boolean => {
        if (!dateFrom || !dateTo) return false
        const d = new Date(dateStr).getTime()
        return (
            d >= new Date(dateFrom).getTime() && d <= new Date(dateTo).getTime()
        )
    }

    const formatDate = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    const handleSearch = async () => {
        if (!dateFrom || !dateTo) return

        const res = await api({
            method: 'GET',
            url: `/admin-dashboard/common-calendar`,
            params: { start: dateFrom, end: dateTo },
        })
        const data = res?.data
        setDialogContent(data)
        setDialogOpen(true)

    }

    return (
        <>
            <Box
                className={dashboard_.card_column}
                mt={1}
                sx={{ padding: '16px !important' }}
            >
                <div className="calendar-container">
                    <div className="calendar-header">
                        <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>{'<'}</button>
                        <span>{currentDate.toLocaleString('default', { month: 'long' })} {year}</span>
                        <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}>{'>'}</button>
                    </div>

                    <div className="calendar-grid">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                            <div key={d} className="day-label">{d}</div>
                        ))}

                        {Array(startDay).fill(null).map((_, i) => (
                            <div key={`empty-${i - 1}`} className="day empty" />
                        ))}

                        {Array.from({ length: days }, (_, i) => {
                            const date = new Date(year, month, i + 1)
                            const dateStr = formatDate(date)
                            const isToday = dateStr === getToday()
                            const selected =
                                dateStr === dateFrom || dateStr === dateTo || isInRange(dateStr)

                            return (
                                <div
                                    key={dateStr}
                                    className={`day${isToday ? ' today' : ''}${selected ? ' selected' : ''}`}
                                    onClick={() => handleDateClick(dateStr)}
                                >
                                    {i + 1}
                                    {badges[dateStr] && (
                                        <span className="badge">{badges[dateStr]}</span>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    <div className="calendar-footer">
                        <div>From: <b>{dateFrom || '-'}</b></div>
                        <div>To: <b>{dateTo || '-'}</b></div>
                        <button
                            className="search-button"
                            disabled={!dateFrom || !dateTo}
                            onClick={handleSearch}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </Box>
            <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth={false} maxWidth="sm"
                PaperProps={{
                    sx: { width: '70%', maxWidth: '70%' }, // ширина 70% экрана
                }}>
                {/* <DialogTitle>📊 {dateFrom} - {dateTo} Total results: {calculateTotal(dialogContent)} </DialogTitle> */}
                <DialogContent>
                    <UsageStatsAll data={dialogContent} monthRange={ {start: dateFrom, end: dateTo} } />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Calendar
